import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { useToast } from "../components/ui/Feedback";
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import { friendlyError } from "../lib/errorMessage";

export default function HairTips() {
  const toast = useToast();
  const [tips, setTips] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ category: '', tip: '', is_active: true });
  const [adding, setAdding] = useState(false);
  const [newTip, setNewTip] = useState({ category: 'general', tip: '', is_active: true });

  useEffect(() => { fetchTips(); }, []);

  async function fetchTips() {
    const { data } = await supabase.from('hair_tips').select('*').order('created_at', { ascending: false });
    setTips(data ?? []);
    setLoading(false);
  }

  async function saveTip(id: string) {
    const { error } = await supabase.from('hair_tips').update(editValues).eq('id', id);
    if (error) { toast.failure(friendlyError(error, "Something went wrong. Please try again.")); return; }
    toast.success('Tip updated');
    setTips(t => t.map(x => x.id === id ? { ...x, ...editValues } : x));
    setEditing(null);
  }

  async function deleteTip(id: string) {
    if (!window.confirm('Delete this tip?')) return;
    const { error } = await supabase.from('hair_tips').delete().eq('id', id);
    if (error) { toast.failure(friendlyError(error, "Something went wrong. Please try again.")); return; }
    toast.success('Tip deleted');
    setTips(t => t.filter(x => x.id !== id));
  }

  async function addTip() {
    if (!newTip.category || !newTip.tip) return;
    const { data } = await supabase.from('hair_tips').insert(newTip).select().single();
    if (data) setTips(t => [data, ...t]);
    setNewTip({ category: 'general', tip: '', is_active: true });
    setAdding(false);
  }

  const startEdit = (tip: any) => {
    setEditing(tip.id);
    setEditValues({ category: tip.category ?? 'general', tip: tip.tip ?? '', is_active: tip.is_active ?? true });
  };

  return (
    <div>
      <Topbar title="Hair Tips" />
      <div style={{ padding: '24px 28px' }}>

        <div className="page-header">
          <div>
            <div className="page-title">Hair Tips</div>
            <div className="page-sub">Manage the daily hair tips sent to all users</div>
          </div>
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <Plus size={15} /> Add Tip
          </button>
        </div>

        {/* Add form */}
        {adding && (
          <div className="card" style={{ marginBottom: 16, borderLeft: '3px solid var(--primary)' }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>New Hair Tip</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="input" placeholder="Category (e.g. moisture, growth, scalp)"
                value={newTip.category} onChange={e => setNewTip(n => ({ ...n, category: e.target.value }))} />
              <textarea className="input" rows={3} placeholder="Tip message..."
                value={newTip.tip} onChange={e => setNewTip(n => ({ ...n, tip: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" onClick={addTip}><Check size={13} /> Save</button>
              <button className="btn btn-sm" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: 'none' }}
                onClick={() => setAdding(false)}><X size={13} /> Cancel</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {tips.map((tip, i) => (
            <div key={tip.id} className="card" style={{ padding: 16 }}>
              {editing === tip.id ? (
                <div>
                  <input className="input" style={{ marginBottom: 8 }} value={editValues.category}
                    onChange={e => setEditValues(v => ({ ...v, category: e.target.value }))} />
                  <textarea className="input" rows={3} value={editValues.tip}
                    onChange={e => setEditValues(v => ({ ...v, tip: e.target.value }))}
                    style={{ resize: 'vertical', marginBottom: 10 }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <input type="checkbox" checked={editValues.is_active}
                      onChange={e => setEditValues(v => ({ ...v, is_active: e.target.checked }))} />
                    Active
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => saveTip(tip.id)}><Check size={13} /> Save</button>
                    <button className="btn btn-sm" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: 'none' }}
                      onClick={() => setEditing(null)}><X size={13} /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    background: 'var(--primary-light)', color: 'var(--primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span className="badge badge-primary">{tip.category}</span>
                      <span className={tip.is_active ? 'badge badge-success' : 'badge badge-warning'}>
                        {tip.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{tip.tip}</div>
                    {tip.created_at && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                        Added {new Date(tip.created_at).toLocaleDateString('en-GB')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-sm" style={{ background: 'var(--primary-light)', color: 'var(--primary)', border: 'none' }}
                      onClick={() => startEdit(tip)}><Pencil size={13} /></button>
                    <button className="btn btn-sm btn-danger" style={{ border: 'none' }}
                      onClick={() => deleteTip(tip.id)}><Trash2 size={13} /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {tips.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 60 }}>
              No hair tips yet. Add your first one!
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
